/**
 * P2-1 智能话术推荐弹框
 *
 * 客户详情页 / 待办卡片"AI 推荐话术"按钮触发。
 * 后端 POST /customers/:id/suggest-message 返回 3-5 条话术 + 时机建议 + 避免话题。
 * 每条话术可一键复制到剪贴板（writeText）。
 */
import { useState, useEffect } from 'react';
import { Modal, message, Spin } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { $ } from '../lib/api';
import { Btn, Pill } from './ui';

interface Suggestion {
  title: string;
  content: string;
  reason: string;
  tone?: string;
}

interface SuggestResult {
  suggestions: Suggestion[];
  bestSendTime?: string | null;
  avoidTopics?: string[];
  context?: { currentStage?: string; daysSinceLastFu?: number | null; hasProfile?: boolean };
}

interface Props {
  open: boolean;
  customerId: number;
  customerName: string;
  onClose: () => void;
}

const TONE_LABEL: Record<string, string> = {
  warm: '温暖', professional: '专业', gentle: '温和', firm: '坚定',
};

/** 任务 A：渲染前再次 dedup（hashSet）— 后端已 dedup，这里是双保险 */
function dedupeSuggestions(list: Suggestion[]): Suggestion[] {
  const seen = new Set<string>();
  const out: Suggestion[] = [];
  for (const s of list ?? []) {
    const key = (s?.content ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

export function SuggestMessageModal({ open, customerId, customerName, onClose }: Props) {
  const [data, setData] = useState<SuggestResult | null>(null);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

  const suggest = useMutation({
    mutationFn: () => $.post<SuggestResult>(`/customers/${customerId}/suggest-message`, {}),
    onSuccess: (r) => setData(r),
  });

  // 打开时自动触发一次
  useEffect(() => {
    if (open && !data) suggest.mutate();
    if (!open) { setData(null); setCopiedIdx(null); }
  }, [open]);

  const copy = (text: string, idx: number) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedIdx(idx);
      message.success('已复制，去微信粘贴吧～');
      setTimeout(() => setCopiedIdx(null), 2000);
    });
  };

  return (
    <Modal
      title={<>AI 推荐话术 <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 400, marginLeft: 8 }}>{customerName} · {data?.context?.currentStage}</span></>}
      open={open}
      width={680}
      onCancel={onClose}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
            {data?.bestSendTime && `📨 ${data.bestSendTime}`}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn kind="default" onClick={() => suggest.mutate()} disabled={suggest.isPending}>
              {suggest.isPending ? '重新生成…' : '换一组'}
            </Btn>
            <Btn kind="primary" onClick={onClose}>完成</Btn>
          </div>
        </div>
      }
    >
      {suggest.isPending && !data && (
        <div style={{ padding: '40px 0', textAlign: 'center' }}><Spin tip="AI 生成中…" /></div>
      )}
      {data && (
        <>
          {data.context?.daysSinceLastFu !== null && data.context?.daysSinceLastFu !== undefined && (
            <div style={{ marginBottom: 12, fontSize: 12, color: 'var(--text-3)' }}>
              距上次跟进 {data.context.daysSinceLastFu} 天 · {data.context.hasProfile ? '基于 AI 画像' : '画像未生成（建议先跟进 3 次）'}
            </div>
          )}
          {dedupeSuggestions(data.suggestions).map((s, i) => (
            <div key={i} style={{
              padding: 12, marginBottom: 10, background: 'var(--bg-2)',
              borderRadius: 6, borderLeft: '3px solid var(--purple)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ fontWeight: 500 }}>{s.title}</span>
                {s.tone && <Pill tone="default">{TONE_LABEL[s.tone] ?? s.tone}</Pill>}
                <div style={{ flex: 1 }} />
                <a
                  onClick={(e) => { e.preventDefault(); copy(s.content, i); }}
                  style={{
                    fontSize: 12, color: copiedIdx === i ? 'var(--success)' : 'var(--purple)',
                    cursor: 'pointer',
                  }}
                >
                  {copiedIdx === i ? '✓ 已复制' : '📋 复制'}
                </a>
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-1)', lineHeight: 1.6, marginBottom: 6 }}>
                {s.content}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{s.reason}</div>
            </div>
          ))}
          {data.avoidTopics && data.avoidTopics.length > 0 && (
            <div style={{
              marginTop: 12, padding: '8px 12px',
              background: 'rgba(255,77,79,0.08)', color: 'var(--danger)',
              borderRadius: 4, fontSize: 12,
            }}>
              ⚠️ 避免话题：{data.avoidTopics.join(' / ')}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
